import { addDays, dateOf, isoDay, weekdayIndex } from '../../utils/plannerDates';

export interface DateWords {
  today: string[];
  tomorrow: string[];
  weekdays: string[][];
  dayFirst: boolean;
}

export interface Bounds {
  min?: number;
  max?: number;
}

const normalize = (text: string): string => text.trim().toLowerCase().replace(/\s+/g, ' ');

const within = (value: number | null, { min = -Infinity, max = Infinity }: Bounds): number | null =>
  value === null || Number.isNaN(value) || value < min || value > max ? null : value;

function calendarDay(year: number, month: number, day: number): string | null {
  const date = new Date(year, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day ? isoDay(date) : null;
}

export function parseDate(text: string, today: string, words: DateWords): string | null {
  const value = normalize(text);
  if (!value) return null;
  if (words.today.some((word) => normalize(word) === value)) return today;
  if (words.tomorrow.some((word) => normalize(word) === value)) return addDays(today, 1);

  const weekday = words.weekdays.findIndex((names) => names.some((name) => normalize(name) === value));
  if (weekday >= 0) return addDays(today, (weekday - weekdayIndex(today) + 7) % 7);

  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return calendarDay(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const match = value.match(/^(\d{1,2})[./-](\d{1,2})(?:[./-](\d{2}|\d{4}))?$/);
  if (!match) return null;
  const first = Number(match[1]);
  const second = Number(match[2]);
  const day = words.dayFirst ? first : second;
  const month = words.dayFirst ? second : first;
  if (match[3]) {
    const year = Number(match[3]) < 100 ? 2000 + Number(match[3]) : Number(match[3]);
    return calendarDay(year, month, day);
  }
  const year = dateOf(today).getFullYear();
  const thisYear = calendarDay(year, month, day);
  if (!thisYear) return null;
  return thisYear < today ? calendarDay(year + 1, month, day) : thisYear;
}

export function parseTime(text: string): number | null {
  const match = text.trim().match(/^(\d{1,2})(?:[:.]?(\d{2}))?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  return hours > 23 || minutes > 59 ? null : hours * 60 + minutes;
}

const SPOKEN_LENGTHS: Record<string, number> = {
  'רבע שעה': 15,
  'חצי שעה': 30,
  'שעה': 60,
  'שעה ורבע': 75,
  'שעה וחצי': 90,
  'שעתיים': 120,
  'שעתיים וחצי': 150,
  'ساعة': 60,
  'نصف ساعة': 30,
  'ساعة ونصف': 90,
  'ساعتان': 120,
};

const HOUR_UNITS = '(?:h|hr|hrs|hours?|שעות|שעה|ש׳|ש\'|ساعة|ساعات|س)';
const MINUTE_UNITS = '(?:m|min|mins|minutes?|דקות|דק׳|דק\'|דק|دقيقة|دقائق|د)';
const HOURS_PATTERN = new RegExp(
  `^(\\d+(?:\\.\\d+)?)\\s*${HOUR_UNITS}\\s*(?:ו-?|و)?\\s*(?:(\\d+)\\s*${MINUTE_UNITS}?)?$`,
);
const MINUTES_PATTERN = new RegExp(`^(\\d+)\\s*${MINUTE_UNITS}?$`);

export function parseLength(text: string, bounds: Bounds = { min: 1, max: 600 }): number | null {
  const value = normalize(text);
  if (value in SPOKEN_LENGTHS) return within(SPOKEN_LENGTHS[value], bounds);
  let match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (match) return within(Number(match[1]) * 60 + Number(match[2]), bounds);
  match = value.match(HOURS_PATTERN);
  if (match) return within(Math.round(Number(match[1]) * 60) + Number(match[2] ?? 0), bounds);
  match = value.match(MINUTES_PATTERN);
  if (match) return within(Number(match[1]), bounds);
  return null;
}

export function parseCount(text: string, bounds: Bounds): number | null {
  const match = text.match(/\d+/);
  return match ? within(Number(match[0]), bounds) : null;
}

export function resolveTyped<T>(
  text: string,
  current: T | null,
  parse: (text: string) => T | null,
  clearable: boolean,
): { value: T | null; rejected: boolean } {
  if (!text.trim()) return { value: clearable ? null : current, rejected: false };
  const parsed = parse(text);
  return parsed === null ? { value: current, rejected: true } : { value: parsed, rejected: false };
}
